import { TooltipGroupAnchor, TooltipProps } from '@/components/shared/tooltip/TooltipGroup';
import React, { useMemo } from 'react';
import { useTableSearchHighlightTerms } from '@/storybook/context/TableSearchHighlightContext';
import { normalizeSearchTerms } from '@/storybook/utils/normalizeSearchTerms';
import { buildFocusedSearchText, getFocusedSearchSlice, renderHighlightedText } from './searchHighlightUtils';
import { highlightNodeText, renderFocusedHighlightedNode } from './searchHighlightNodes';
import styles from './TruncatedText.module.css';

export type TruncatedTextProps = {
  value: string;
  isTruncatedOptions?: TooltipProps['isTruncatedOptions'];
  tooltipPlace?: TooltipProps['place'];
  tooltipClassName?: TooltipProps['className'];
  displayTooltipForIcon?: TooltipProps['forceDisplayTooltip'];
  highlightValue?: string;
  highlightTerms?: string[];
  children?: React.ReactNode;
};

const DEFAULT_TOOLTIP_OPTIONS: NonNullable<TooltipProps['isTruncatedOptions']> = {
  ignoreWindowResize: true,
};

const MEASUREMENT_NODE_SELECTOR = '[data-truncated-text-measurement="true"]';

const TruncatedText: React.FC<TruncatedTextProps> = ({
  value,
  isTruncatedOptions = DEFAULT_TOOLTIP_OPTIONS,
  tooltipPlace,
  tooltipClassName,
  displayTooltipForIcon,
  highlightValue,
  highlightTerms,
  children,
}) => {
  const contextHighlightTerms = useTableSearchHighlightTerms();
  const resolvedHighlightTerms = useMemo(() => {
    if (highlightTerms !== undefined) {
      return highlightTerms;
    }

    if (highlightValue !== undefined) {
      return normalizeSearchTerms(highlightValue);
    }

    return contextHighlightTerms;
  }, [contextHighlightTerms, highlightTerms, highlightValue]);
  const hasSearchHighlight = resolvedHighlightTerms.length > 0;

  const resolvedIsTruncatedOptions = useMemo(() => {
    if (!hasSearchHighlight) {
      return isTruncatedOptions;
    }

    const customGetTargetNode = isTruncatedOptions.getTargetNode;

    return {
      ...isTruncatedOptions,
      getTargetNode: (refNode: Element) => {
        const measurementNode = refNode.querySelector(MEASUREMENT_NODE_SELECTOR);

        if (measurementNode === null) {
          return customGetTargetNode ? customGetTargetNode(refNode) : refNode;
        }

        return customGetTargetNode ? (customGetTargetNode(measurementNode) ?? measurementNode) : measurementNode;
      },
    };
  }, [hasSearchHighlight, isTruncatedOptions]);

  return (
    <div data-testid="truncated-text-wrapper" className={styles.wrapper}>
      <div className={styles.truncated}>
        <TooltipGroupAnchor
          value={value}
          isTruncatedOptions={resolvedIsTruncatedOptions}
          place={tooltipPlace}
          className={tooltipClassName}
          forceDisplayTooltip={displayTooltipForIcon}
        >
          {({ isTruncated }) => {
            const renderedContent = (() => {
              if (children !== undefined) {
                if (!hasSearchHighlight) {
                  return children;
                }

                return isTruncated
                  ? renderFocusedHighlightedNode(
                      children,
                      getFocusedSearchSlice(value, resolvedHighlightTerms),
                      resolvedHighlightTerms,
                      styles.highlight,
                    )
                  : highlightNodeText(children, resolvedHighlightTerms, styles.highlight);
              }

              const displayValue = isTruncated ? buildFocusedSearchText(value, resolvedHighlightTerms) : value;

              return renderHighlightedText(displayValue, resolvedHighlightTerms, styles.highlight);
            })();

            return (
              <>
                {hasSearchHighlight && (
                  <span
                    aria-hidden="true"
                    data-truncated-text-measurement="true"
                    data-measurement-value={value}
                    className={styles.measurement}
                  />
                )}
                <span className={styles.content}>{renderedContent}</span>
              </>
            );
          }}
        </TooltipGroupAnchor>
      </div>
    </div>
  );
};

export default TruncatedText;
